import {inject, Injectable} from '@angular/core';
import {LoggerService} from "../../services/logger/logger-service/logger.service";
import {MSAL_GUARD_CONFIG, MsalBroadcastService, MsalGuardConfiguration, MsalService} from '@azure/msal-angular';
import {
  AuthenticationResult,
  EventMessage,
  EventType,
  IdTokenClaims,
  InteractionStatus,
  RedirectRequest
} from '@azure/msal-browser';
import {AuthService} from "../auth-service.interface";
import {filter, Subject, takeUntil} from "rxjs";
import {environment} from "../../../../environments/environment";
import {UserService} from "../user-service/user.service";
import {Router} from "@angular/router";
import {TokenService} from "../token-service/token.service";
import {AuditLogsService} from "../../services/audit-logs-service/audit-logs.service";

type IdTokenClaimsWithPolicyId = IdTokenClaims & {
  acr?: string,
  tfp?: string,
};

/**
 * Uses MSAL to authenticate users and handle roles.
 */
@Injectable({
  providedIn: 'root'
})
export class AppMsalAuthService implements AuthService {
  private readonly _destroying$ = new Subject<void>();

  // Injectables
  private logger = inject(LoggerService);
  private msalService = inject(MsalService);
  private msalBroadcastService = inject(MsalBroadcastService);
  private msalGuardConfig = inject<MsalGuardConfiguration>(MSAL_GUARD_CONFIG);
  private userService = inject(UserService);
  private router = inject(Router);
  private tokenService = inject(TokenService);
  private auditLogsService = inject(AuditLogsService);

  constructor() {
    this.setupSubscriptions();
  }

  login() {
      this.msalService.loginRedirect({...this.msalGuardConfig.authRequest} as RedirectRequest);
  }

  logout() {
    this.auditLogsService.logLogout();
    this.userService.resetUser();
    this.msalService.logoutRedirect();
  }

  isLoggedIn(): boolean {
    return this.msalService.instance.getAllAccounts().length > 0; // this.user() !== null;
  }

  private async setTokenFromActiveAccount() {
    if (this.tokenService.getToken()) {
      return;
    }

    const account = this.msalService.instance.getActiveAccount();
    if (account === null) {
      return;
    }

    const token = await this.msalService.instance.acquireTokenSilent({
      scopes: environment.apiConfig.scopes,
      account
    })
    this.tokenService.setToken(token.accessToken)
  }

  private setupSubscriptions(): void {
    this.msalService.handleRedirectObservable().subscribe(); // Subscribing to handleRedirectObservable before any other functions both initializes the application and ensures redirects are handled

    this.msalBroadcastService.inProgress$
      .pipe(
        filter((status: InteractionStatus) => status === InteractionStatus.None),
        takeUntil(this._destroying$)
      )
      .subscribe(() => {
        this.checkAndSetActiveAccount();
      })

    this.msalBroadcastService.msalSubject$
      .pipe(
        filter((msg: EventMessage) => msg.eventType === EventType.LOGIN_SUCCESS
          || msg.eventType === EventType.ACQUIRE_TOKEN_SUCCESS
          || msg.eventType === EventType.SSO_SILENT_SUCCESS),
        takeUntil(this._destroying$)
      )
      .subscribe((result: EventMessage) => {
        this.checkAndSetActiveAccount();

        let payload = result.payload as AuthenticationResult;
        let idtoken = payload.idTokenClaims as IdTokenClaimsWithPolicyId;

        if (idtoken.acr === environment.b2cPolicies.names.signUpSignIn || idtoken.tfp === environment.b2cPolicies.names.signUpSignIn) {
          this.msalService.instance.setActiveAccount(payload.account);
        }

        if (result.eventType === EventType.LOGIN_SUCCESS) {
          this.router.navigate([this.router.url]); // Manual refresh to ensure page properly loads
        }
        return result;
      });
  }

  private checkAndSetActiveAccount() {
    /**
     * If no active account set but there are accounts signed in, sets first account to active account
     * To use active account set here, subscribe to inProgress$ first in your component
     * Note: Basic usage demonstrated. Your app may require more complicated account selection logic
     */
    let activeAccount = this.msalService.instance.getActiveAccount();

    if (
      !activeAccount &&
      this.msalService.instance.getAllAccounts().length > 0
    ) {
      let accounts = this.msalService.instance.getAllAccounts();
      this.msalService.instance.setActiveAccount(accounts[0]);
    }

    this.setTokenFromActiveAccount();
  }
}
